import React, { useCallback, useRef, useState } from 'react';

interface VolumeBarProps {
  /** 0..1 */
  level: number;
  onChange: (next: number) => void;
}

/**
 * Hover-expand volume bar with pointer-capture drag. The parent's `oden-vol-
 * cluster:hover` rule still drives the width-grow animation; this component
 * only handles the pointer interaction and the fill transform.
 */
const VolumeBar: React.FC<VolumeBarProps> = ({ level, onChange }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [hoverRatio, setHoverRatio] = useState<number | null>(null);

  const ratioAt = useCallback((clientX: number, rect: DOMRect) => {
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    return rect.width > 0 ? x / rect.width : 0;
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    e.preventDefault();
    ref.current.setPointerCapture(e.pointerId);
    setDragging(true);
    const rect = ref.current.getBoundingClientRect();
    onChange(ratioAt(e.clientX, rect));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const r = ratioAt(e.clientX, rect);
    setHoverRatio(r);
    if (dragging) onChange(r);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (ref.current) ref.current.releasePointerCapture(e.pointerId);
    setDragging(false);
  };

  const handlePointerLeave = () => {
    if (!dragging) setHoverRatio(null);
  };

  const pct = Math.max(0, Math.min(1, level));

  // Hover preview band: light purple between the current level and cursor.
  // Suppressed if there's no measurable delta so we don't render a 0-width
  // sliver right on top of the level fill.
  let hoverBand: { left: number; width: number } | null = null;
  if (hoverRatio !== null) {
    const a = Math.min(pct, hoverRatio);
    const b = Math.max(pct, hoverRatio);
    if (b - a > 0.001) hoverBand = { left: a, width: b - a };
  }

  return (
    <div
      ref={ref}
      role="slider"
      aria-label="Volume"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct * 100)}
      tabIndex={-1}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      className="oden-vol-bar"
    >
      <div className="oden-vol-bar-fill" style={{ width: `${pct * 100}%` }} />
      {hoverBand && (
        <div
          className="oden-vol-bar-hover"
          style={{ left: `${hoverBand.left * 100}%`, width: `${hoverBand.width * 100}%` }}
        />
      )}
    </div>
  );
};

export default VolumeBar;
